/**
 * OsintClarificationCard — shown while an OSINT run is parked in
 * `awaiting_input`: the agent couldn't confidently identify the subject and has
 * asked the user for a missing identifier. Submitting resumes the same
 * investigation with the answers, "Finish with what you have" resumes it
 * straight to a report. Both flip the run back to `running`, after which the
 * live progress panel takes over again.
 *
 * Two shapes of ask, one card. Without `options` it is what it always was — a
 * text input per question. With `options`, the agent is holding several
 * candidates it cannot tell apart (namesakes, or names differing only by
 * diacritics) and the first question becomes a pick: the user chooses the actual
 * record, and only its id goes back, so the identifier the run locks onto is the
 * register's own rather than something retyped.
 *
 * Candidates arrive from two places and are NEVER mixed in one list. Register
 * rows are the register's own data. Web candidates are people the agent read off
 * an open-web page — necessary, because plenty of OSINT subjects appear in no
 * register at all, but nobody has verified they exist as described, and picking
 * one yields an identity the report must not call confirmed. Rendering them
 * identically would ask the user to stake an investigation on that difference
 * without showing it to them.
 */

import { HelpCircle, Send } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ClarificationCard } from '@/components/agent/ClarificationCard'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { toast } from '@/components/ui/sonner'
import { useAnswerOsintClarification, useFinalizeOsintNow } from '@/features/osint/hooks/use-osint'
import type { OsintClarification, OsintClarificationOption } from '@/services/api/osint'

interface Props {
  caseId: string
  osintId: string
  clarification: OsintClarification
}

/** Sentinel for "none of these" — never sent as a selectedId. */
const NONE = '__none__'

/** Heading that keeps register rows and open-web guesses visibly apart. */
function GroupLabel({
  children,
  tone = 'default',
}: {
  children: React.ReactNode
  tone?: 'default' | 'warning'
}) {
  return (
    <p
      className={
        tone === 'warning'
          ? 'mt-1 text-xs font-medium text-warning'
          : 'mt-1 text-xs font-medium text-muted-foreground'
      }
    >
      {children}
    </p>
  )
}

/** One pickable candidate. Web-sourced rows carry an explicit unverified badge. */
function OptionCard({
  option,
  picked,
  busy,
  onPick,
}: {
  option: OsintClarificationOption
  picked: boolean
  busy: boolean
  onPick: (id: string) => void
}) {
  const { t } = useTranslation()
  const isWeb = option.origin === 'web'
  return (
    <Card
      size="compact"
      interactive
      selected={picked}
      tone={isWeb ? 'warning' : 'neutral'}
      className="flex items-start gap-3 p-3"
      onClick={() => !busy && onPick(option.id)}
    >
      <RadioGroupItem value={option.id} id={`osint-option-${option.id}`} className="mt-0.5" />
      <div className="min-w-0 flex-1">
        <Label
          htmlFor={`osint-option-${option.id}`}
          className="cursor-pointer text-sm font-medium text-foreground"
        >
          {option.label}
        </Label>
        {option.detail && <p className="mt-0.5 text-sm text-muted-foreground">{option.detail}</p>}
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          {isWeb && (
            <span className="text-warning">{t('osint.clarification.candidates.webBadge')}</span>
          )}
          {option.ico && (
            <span>{t('osint.clarification.candidates.ico', { ico: option.ico })}</span>
          )}
          {option.birthDate && (
            <span>{t('osint.clarification.candidates.born', { date: option.birthDate })}</span>
          )}
          {option.address && <span>{option.address}</span>}
          {option.terminated && (
            <span className="text-warning">{t('osint.clarification.candidates.terminated')}</span>
          )}
        </div>
        {option.recordUrl && (
          <a
            href={option.recordUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="mt-1 inline-block text-xs text-primary hover:underline"
          >
            {isWeb
              ? t('osint.clarification.candidates.viewPage')
              : t('osint.clarification.candidates.viewRecord')}
          </a>
        )}
      </div>
    </Card>
  )
}

export function OsintClarificationCard({ caseId, osintId, clarification }: Props) {
  const { t } = useTranslation()
  const { context, questions } = clarification
  const options = clarification.options ?? []
  const hasOptions = options.length > 0
  // Kept apart all the way to the DOM: a register row and a name off a web page
  // are not interchangeable evidence, and the pick decides the whole run.
  const registerOptions = options.filter(o => o.origin !== 'web')
  const webOptions = options.filter(o => o.origin === 'web')
  const [values, setValues] = useState<string[]>(() => questions.map(() => ''))
  const [picked, setPicked] = useState<string>('')

  const answer = useAnswerOsintClarification(caseId, osintId)
  const finalize = useFinalizeOsintNow(caseId, osintId)
  const busy = answer.isPending || finalize.isPending
  const canSubmit = !busy && (picked !== '' || values.some(v => v.trim().length > 0))

  const submit = async () => {
    if (!canSubmit) return
    const chosen = options.find(o => o.id === picked)
    try {
      await answer.mutateAsync({
        answers: questions.map((question, i) => {
          const typed = values[i]?.trim() ?? ''
          // The pick rides the first question — that's the one the agent
          // attached its candidates to.
          if (i !== 0 || !hasOptions) return { question, answer: typed }
          if (chosen) return { question, answer: chosen.label, selectedId: chosen.id }
          if (picked === NONE) {
            return { question, answer: typed || t('osint.clarification.noneOfTheseAnswer') }
          }
          return { question, answer: typed }
        }),
      })
    } catch {
      toast.error(t('osint.clarification.error'))
    }
  }

  const finishAnyway = async () => {
    if (busy) return
    try {
      await finalize.mutateAsync()
    } catch {
      toast.error(t('osint.clarification.error'))
    }
  }

  if (!hasOptions) {
    return (
      <ClarificationCard
        clarification={{
          context: clarification.context,
          questions: clarification.questions.map(question => ({ question, options: [] })),
        }}
        busy={answer.isPending}
        translationPrefix="osint.clarification"
        onSubmit={async responses => {
          await answer.mutateAsync({
            answers: responses.map(item => ({ question: item.question, answer: item.answer })),
          })
        }}
        secondaryAction={{
          label: t('osint.clarification.finishAnyway'),
          busy: finalize.isPending,
          onClick: async () => finishAnyway(),
        }}
      />
    )
  }

  return (
    <Card tone="primary" className="flex flex-col gap-4 p-4 md:p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <HelpCircle className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-foreground">
            {t('osint.clarification.title')}
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {context || t('osint.clarification.description')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {questions.map((question, i) => (
          <div key={question} className="flex flex-col gap-1.5">
            <Label htmlFor={`osint-clarify-${i}`} className="max-md:leading-snug">
              {question}
            </Label>

            {i === 0 && hasOptions && (
              <RadioGroup
                value={picked}
                onValueChange={setPicked}
                disabled={busy}
                className="gap-2"
                aria-label={question}
              >
                {registerOptions.length > 0 && (
                  <>
                    {webOptions.length > 0 && (
                      <GroupLabel>{t('osint.clarification.candidates.registerGroup')}</GroupLabel>
                    )}
                    {registerOptions.map(option => (
                      <OptionCard
                        key={option.id}
                        option={option}
                        picked={picked === option.id}
                        busy={busy}
                        onPick={setPicked}
                      />
                    ))}
                  </>
                )}
                {webOptions.length > 0 && (
                  <>
                    <GroupLabel tone="warning">
                      {t('osint.clarification.candidates.webGroup')}
                    </GroupLabel>
                    {webOptions.map(option => (
                      <OptionCard
                        key={option.id}
                        option={option}
                        picked={picked === option.id}
                        busy={busy}
                        onPick={setPicked}
                      />
                    ))}
                  </>
                )}
                <Card
                  size="compact"
                  interactive
                  selected={picked === NONE}
                  className="flex items-start gap-3 p-3"
                  onClick={() => !busy && setPicked(NONE)}
                >
                  <RadioGroupItem value={NONE} id="osint-option-none" className="mt-0.5" />
                  <Label
                    htmlFor="osint-option-none"
                    className="cursor-pointer text-sm font-medium text-foreground"
                  >
                    {t('osint.clarification.candidates.noneOfThese')}
                  </Label>
                </Card>
              </RadioGroup>
            )}

            <Input
              id={`osint-clarify-${i}`}
              value={values[i] ?? ''}
              onChange={e =>
                setValues(prev => {
                  const next = [...prev]
                  next[i] = e.target.value
                  return next
                })
              }
              onKeyDown={e => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit()
              }}
              placeholder={
                i === 0 && hasOptions
                  ? t('osint.clarification.candidates.orDescribe')
                  : t('osint.clarification.answerPlaceholder')
              }
              disabled={busy}
              autoComplete="off"
            />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 max-md:flex-col-reverse max-md:items-stretch">
        <Button
          variant="ghost"
          size="sm"
          onClick={finishAnyway}
          loading={finalize.isPending}
          disabled={busy}
        >
          {t('osint.clarification.finishAnyway')}
        </Button>
        <Button onClick={submit} loading={answer.isPending} disabled={!canSubmit}>
          {!answer.isPending && <Send className="size-4" />}
          {t('osint.clarification.submit')}
        </Button>
      </div>
    </Card>
  )
}
